import {
  ApiError,
  getCurrentUserControllerGetCurrentUserQueryOptions,
  partnersAdminControllerCreatePartner,
  partnersAdminControllerGetPartner,
  partnersAdminControllerListPartners,
  partnersAdminControllerUpdatePartner,
} from '@artic/api-client';
import type { CreatePartnerDto, UpdatePartnerDto } from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

export const PARTNERS_KEY = 'partners' as const;

// ─── List (admin) ─────────────────────────────────────────────────

export function partnersListQueryOptions() {
  return queryOptions({
    queryKey: [PARTNERS_KEY, 'list'] as const,
    queryFn: async ({ signal }) => {
      const res = await partnersAdminControllerListPartners({ signal });
      return res.data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Detail (admin), keyed by partner_id ──────────────────────────

export function partnerDetailQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [PARTNERS_KEY, 'detail', partnerId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await partnersAdminControllerGetPartner(partnerId, { signal });
      return res.data;
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Conflict surface ─────────────────────────────────────────────

/**
 * Typed 409 surface from the admin create endpoint so the create sheet can pin
 * the error to the offending input instead of only a top-level message.
 */
export class PartnerConflictError extends Error {
  public readonly field: 'partner_name' | 'partner_key';

  constructor(field: 'partner_name' | 'partner_key', message: string) {
    super(message);
    this.name = 'PartnerConflictError';
    this.field = field;
  }
}

function mapPartnerConflict(err: unknown): never {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.bodyAsRecord;
    const rawField = body?.['field'];
    const rawMessage = body?.['message'];
    const message = typeof rawMessage === 'string' ? rawMessage : err.message;
    if (rawField === 'partner_name' || rawField === 'partner_key') {
      throw new PartnerConflictError(rawField, message);
    }
  }
  throw err;
}

// ─── Create (mutation) ────────────────────────────────────────────

export type CreatePartnerParams = CreatePartnerDto;

export function useCreatePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreatePartnerParams) => {
      try {
        const res = await partnersAdminControllerCreatePartner(params);
        return res.data;
      } catch (err: unknown) {
        mapPartnerConflict(err);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [PARTNERS_KEY] });
      // The new partner is now linked to every Artic staff member, so refresh
      // the current user — it makes the partner appear in the switcher at once.
      void queryClient.invalidateQueries({
        queryKey: getCurrentUserControllerGetCurrentUserQueryOptions().queryKey,
      });
    },
  });
}

// ─── Update (mutation), keyed by partner_id ───────────────────────

export type UpdatePartnerParams = UpdatePartnerDto;

export function useUpdatePartner(partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdatePartnerParams) => {
      const res = await partnersAdminControllerUpdatePartner(partnerId, params);
      return res.data;
    },
    onSuccess: (data) => {
      // Write the fresh detail straight into cache for an instant header/tab
      // refresh, then invalidate the prefix to refresh the list view.
      queryClient.setQueryData([PARTNERS_KEY, 'detail', partnerId], data);
      void queryClient.invalidateQueries({ queryKey: [PARTNERS_KEY] });
    },
  });
}
